import Trip from 'model/ITrip';
import { getAssignedExperiments, reqWithABTest } from 'utils/abTesting';
import { getExperimentByName } from 'utils/abTests';
import req from 'utils/req';

const bookingsApiUrl2 = `${URL_API_HOST}/api/v2/bookings`;
const bookingsApiUrl3 = `${URL_API_HOST}/api/v3/bookings`;

export default {
  getOne: (bookingId) => {
    return req(`${bookingsApiUrl2}/${bookingId}`)
      .get()
      .then((booking) => Object.assign(booking, { isFullObject: true }));
  },
};

function getSavedSelectedLangCode(experienceId) {
  try {
    return window.sessionStorage.getItem(`host-langcode-${experienceId}`);
  } catch (e) {
    return null;
  }
}

export function postDryRunCustomTrip(experienceId, nrOfGuests) {
  const bodyObj = {
    nrOfGuests,
    experienceId: experienceId,
  };

  return req(`${URL_API_HOST}/api/v1/customtrips/dryrun`).post(bodyObj);
}

export function postBooking({
  experienceId,
  hostId,
  dateTime,
  numberOfPeople,
  numberOfChildren,
  tripType = 'PRIVATE',
  coupon_codes = null,
  packageId = null,
  packageDate = null,
  experienceExtrasIds = [],
  preferredCurrency = 'EUR',
}) {
  const urlParams = new URLSearchParams(window.location.search);

  const bookingObj = {
    coupon_codes,
    dateTime,
    numberOfPeople,
    numberOfChildren,
    packageId: packageId || urlParams.get('packageId'),
    packageDate: packageDate || urlParams.get('packageDate'),
    experience_id: experienceId,
    host_id: hostId,
    trip_type: tripType,
    abTests: getAssignedExperiments(),
    experienceExtrasIds,
    preferredCurrency,
  } as any;

  const preferredHostLanguage = getSavedSelectedLangCode(experienceId);
  if (preferredHostLanguage) {
    bookingObj.preferred_host_language = preferredHostLanguage;
  }

  const searchParams = new URLSearchParams({
    isServiceFeeGroup: 'GroupA',
  });

  if (!preferredCurrency) {
    console.error('No currency provided for booking');
  }
  searchParams.set('currency', preferredCurrency);

  const url = `${URL_API_HOST}/api/v1/trips/reservation?${searchParams.toString()}`;
  const italianCitiesPricing = getExperimentByName('italianCitiesPricing');
  return reqWithABTest(req(url).post, italianCitiesPricing.id)(bookingObj);
}

export function postDryRunBooking({
  experienceId,
  hostId,
  dateTime,
  numberOfPeople,
  numberOfChildren,
  tripType = 'PRIVATE',
  coupon_codes = [],
  experienceExtrasIds = [],
  currency,
}) {
  const urlParams = new URLSearchParams(window.location.search);
  const packageId = urlParams.get('packageId');
  const packageDate = urlParams.get('packageDate');

  const bookingObj = {
    coupon_codes,
    dateTime,
    numberOfPeople,
    numberOfChildren,
    packageId,
    packageDate,
    experienceExtrasIds,
    experience_id: experienceId,
    host_id: hostId,
    trip_type: tripType,
    preferredCurrency: currency,
  };

  const searchParams = new URLSearchParams({
    isServiceFeeGroup: 'GroupA',
  });

  if (!currency) {
    console.error('No currency provided for booking');
  }
  searchParams.set('currency', currency);

  const url = `${URL_API_HOST}/api/v1/trips/dryrun?${searchParams.toString()}`;
  const italianCitiesPricing = getExperimentByName('italianCitiesPricing');
  return reqWithABTest(req(url).post, italianCitiesPricing.id)(bookingObj);
}

export function fetchUpcomingBookings({
  page,
  pageSize,
  onlyConfirmed = false,
}) {
  const query = new URLSearchParams();
  query.set('page', page);
  query.set('per_page', pageSize);
  if (onlyConfirmed) {
    query.set('only_confirmed', '1');
  }

  return req(`${bookingsApiUrl3}/upcoming?${query.toString()}`).get();
}

export function fetchArchivedBookings({ page, pageSize }) {
  const query = new URLSearchParams();
  query.set('page', page);
  query.set('per_page', pageSize);

  return req(`${bookingsApiUrl3}/previous?${query.toString()}`).get();
}

export function fetchBookingsFromContact({ accountId, page, pageSize }) {
  const query = new URLSearchParams();
  query.set('page', page);
  query.set('per_page', pageSize);

  return req(
    `${bookingsApiUrl3}/${accountId}/bookings?${query.toString()}`,
  ).get();
}

export type ReturnFetchBookingsFromContacts = {
  meta: {
    totalPages: string;
    pageSize: string;
    totalHits: string;
    page: string;
  };
  data: {
    id: string;
    contact: string;
    lastModified: string;
    firstUpcoming?: string;
    firstActionable?: string;
    total: number;
  }[];
  relations: (
    | (Trip & { _type: 'booking' })
    | { id: string; _type: 'contact' }
  )[];
};

export function fetchBookingsFromContacts({
  contactIds,
}: {
  contactIds: string[];
}): Promise<ReturnFetchBookingsFromContacts> {
  const query = contactIds.reduce((accumulator, currentValue) => {
    accumulator.append('ids', currentValue);
    return accumulator;
  }, new URLSearchParams());

  query.set('include', 'booking');
  query.set('page', '1');
  query.set('per_page', '10');

  return req(`${bookingsApiUrl3}?${query.toString()}`).get();
}

export interface BookingsResponse {
  data: Trip;
  relations: [];
  links: {
    self: string;
  };
  meta: { type: 'booking' };
}

export function fetchBooking(id: string): Promise<BookingsResponse> {
  return req(`${URL_API_HOST}/api/v3/booking/${id}`).get();
}

export function createBookingTransferRequest(
  bookingId,
  potentialHosts,
  cancelExplanation,
) {
  return req(`${URL_API_HOST}/api/v1/booking/transfer/transferRequest`).post({
    bookingId,
    potentialHosts,
    cancelExplanation,
  });
}

export function getBookingTransferRequest(transferRequestId) {
  return req(
    `${URL_API_HOST}/api/v1/booking/transfer/transferRequest/${transferRequestId}`,
  ).get();
}

export function acceptBookingTransfer(transferRequestId) {
  return req(
    `${URL_API_HOST}/api/v1/booking/transfer/transferRequest/${transferRequestId}/accept`,
  ).post();
}

export function rejectBookingTransfer(transferRequestId) {
  return req(
    `${URL_API_HOST}/api/v1/booking/transfer/transferRequest/${transferRequestId}/reject`,
  ).post();
}

export function fetchTripRescheduleAvailableDays(tripId) {
  return req(
    `${URL_API_HOST}/api/v1/booking/reschedule/${tripId}/availabledays`,
  ).get();
}

export function fetchTripRescheduleAvailableTimes(tripId, rescheduleDate) {
  return req(
    `${URL_API_HOST}/api/v1/booking/reschedule/${tripId}/availabletimes/${rescheduleDate}`,
  ).get();
}

export function confirmBooking({ id }): Promise<Object> {
  return req(`${URL_API_HOST}/api/v1/bookings/approve/${id}`).post();
}

export function cancelBookingAsHost({
  id,
  cancelExplanation,
}: {
  id: string;
  cancelExplanation: string;
}): Promise<any> {
  return req(`${URL_API_HOST}/api/v2/trip/cancellation/${id}`).put({
    cancelExplanation,
  });
}

export function cancelOrTransferBookingAsHost({
  id,
  cancelExplanation,
}: {
  id: string;
  cancelExplanation: string;
}): Promise<any> {
  return req(`${URL_API_HOST}/api/v2/trip/cancellation-transfer/${id}`).put({
    cancelExplanation,
  });
}

export function rejectBooking({ id }: { id: string }): Promise<any> {
  return req(`${URL_API_HOST}/api/v2/trip/${id}`).delete();
}

export function withdrawOffer({ id }): Promise<Object> {
  return req(`${URL_API_HOST}/api/v1/customtrips/${id}`).delete();
}

export function reschedule({ id, date }: { id: string; date: string }) {
  return req(`${URL_API_HOST}/api/v1/trips/${id}`).put({ trip_date: date });
}

export function rejectReschedule({ requestId }: { requestId: string }) {
  return req(`${URL_API_HOST}/api/v1/userrequest/${requestId}`).delete();
}

interface Offer {
  id?: string;
  experience_id?: string;
  guest_id: string;
  host_id?: string;
  trip_date: string;
  trip_details: {};
  address: {};
  price: number;
  number_of_guest: number;
  status: string;
  user_request_id?: string;
  coupon?: string;
}

export function putOffer({ id, offer }: { id: string; offer: Offer }) {
  return req(`${URL_API_HOST}/api/v1/customtrips/${id}`).put(offer);
}

export function postOffer({ offer }: { offer: Offer }) {
  return req(
    `${URL_API_HOST}/api/v1/customtrips?isServiceFeeGroup=GroupA`,
  ).post(offer);
}

export function putProposal(bookingId: string, preferredCurrency: string) {
  const bookingObj = {
    preferredCurrency,
  } as any;

  const url = `${URL_API_HOST}/api/v1/trips/proposal/preferred/currency/${bookingId}?isServiceFeeGroup=GroupA`;
  const italianCitiesPricing = getExperimentByName('italianCitiesPricing');
  return reqWithABTest(req(url).put, italianCitiesPricing.id)(bookingObj);
}
